import { getQueryParameter } from '@atabix/core'
import type { NavigationGuardWithThis } from 'vue-router'

import { useAuthDriver } from '@/auth'
import { ApplicationState } from '@/enums/ApplicationState'
import { FetchError } from '@/plugins/http'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useSettingsStore } from '@/stores/settings'
import { useTokenStore } from '@/stores/token'

const InitializeAppMiddleware: NavigationGuardWithThis<undefined> = async function (to, from, next) {
  const app = useAppStore()
  const settings = useSettingsStore()
  const auth = useAuthStore()
  const token = useTokenStore()
  if (app.isInitialized || app.isReady) return true

  app.setState(ApplicationState.INITIALIZING)

  try {
    const versionId = getQueryParameter('version') || undefined

    await settings.teapot(versionId)

    if (import.meta.env.VITE_DEV_MOCK_AUTH === 'true') {
      auth.setUser({
        id: 'dev-user-id',
        email: 'dev@atabix.nl',
        name: 'Dev Atabix',
        first_name: 'Dev',
        last_name: 'Atabix',
        is_active: true,
        is_root: true,
        avatar: 'https://www.gravatar.com/avatar/dev?d=identicon&s=128',
        permissions: [],
        roles: [],
      } as never)
    } else {
      // Save the deep route the user originally tried to visit so we can return to
      // it after the Keycloak round-trip (which always lands on '/'). Don't
      // overwrite an existing saved redirect — that's the original target from
      // before the auth bounce. The auth callback URL has the code in the hash
      // and Vue Router redirects '/' → '/home', so we'd otherwise overwrite it.
      const target = to.fullPath
      if (target && target !== '/' && target !== '/home' && !token.redirect) {
        token.setRedirect(target)
      }

      await useAuthDriver().driver.session()

      // After auth completes, navigate back to the original deep route via the
      // token store's useRedirect (which is setTimeout-deferred so it runs once
      // the current vue-router navigation is finalised — RouteMiddlewareFactory
      // ignores guard return values, so we can't redirect inline).
      // Always clear the redirect after initialization — either by consuming it
      // (paths differ) or by discarding it (already on the right page). Without
      // this, a redirect saved on load A stays in localStorage and hijacks load B.
      if (token.access && token.redirect) {
        if (token.redirect !== to.fullPath) {
          token.useRedirect()
        } else {
          token.setRedirect('')
        }
      }
    }

    app.setState(ApplicationState.INITIALIZED)
  } catch (error) {
    if (error instanceof FetchError) {
      if (error.status === 404) {
        setTimeout(() => app.setState(ApplicationState.UNKNOWN), 1000)
        return false
      }

      if (error.status === 503) {
        setTimeout(() => app.setState(ApplicationState.MAINTENANCE), 1000)
        return false
      }
    }

    console.error(error)
    app.setState(ApplicationState.FATAL)
    return false
  }
}

export default InitializeAppMiddleware
